import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/log";
import { api, startCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WebOnlyPurchase } from "@/components/WebOnlyPurchase";
import { PayByInvoiceLink } from "@/components/PayByInvoice";
import { Price } from "@/components/Price";
import { Checkbox } from "@/components/ui/checkbox";
import { Rocket, Check, Loader2, Lock, ArrowRight, Package, Eye } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Startup() {
  const { user } = useAuth();
  const [steps, setSteps] = useState([]);
  const TRACKS = ["care", "aesthetics", "healthcare", "fostering"];
  const [track, setTrack] = useState(() => { const t = new URLSearchParams(window.location.search).get("track"); return TRACKS.includes(t) ? t : "care"; });
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [toolkit, setToolkit] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/startup/steps", { params: { track } }).then(({ data }) => setSteps(data)).finally(() => setLoading(false));
  }, [track]);

  useEffect(() => {
    api.get("/resources").then(({ data }) => setToolkit(data.find((r) => r.lookup_key === "startup_toolkit") || null)).catch((err) => logError("Failed to load toolkit", err));
    if (user) api.get("/me/startup-progress").then(({ data }) => setProgress(data.progress || [])).catch((err) => logError("Failed to load progress", err));
  }, [user]);

  const toggle = async (stepId, done) => {
    if (!user) { toast.error("Sign in to track your progress"); return; }
    setProgress((p) => (done ? [...p, stepId] : p.filter((s) => s !== stepId)));
    try {
      await api.post("/me/startup-progress", { step_id: stepId, completed: done });
    } catch {
      toast.error("Could not save progress");
    }
  };

  const pct = steps.length ? Math.round((steps.filter((st) => progress.includes(st.id)).length / steps.length) * 100) : 0;

  const buyToolkit = async () => {
    if (!user) { toast.error("Sign in to purchase"); return; }
    setBuying(true);
    try { await startCheckout("startup_toolkit"); }
    catch { toast.error("Could not start checkout"); setBuying(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="rounded-3xl bg-gradient-to-r from-amber-500 to-orange-600 p-8 lg:p-12 text-white mb-10 relative overflow-hidden">
        <Rocket className="w-10 h-10 mb-4 opacity-90" />
        <span className="overline text-white/80">New Start-ups</span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 mb-3">
          Your registration roadmap
        </h1>
        <p className="text-lg text-white/90 max-w-xl">
          {{
            aesthetics: "A guided path to CQC registration for aesthetics and cosmetic clinics — from working out whether you need to register to your fit-person interview.",
            fostering: "A guided path to Ofsted registration for a new independent fostering agency — from legal entity and responsible individual to panel, Form F and your fit-person interviews.",
            healthcare: "A guided path to CQC registration for GP practices, independent hospitals and clinics — from choosing regulated activities to clinical governance and your fit-person interview.",
          }[track] || "A guided, step-by-step path to CQC / Ofsted registration. Automatic alerts on changes to CQC and Ofsted regulations and guidance, so you're notified when something relevant changes."}
        </p>
        <div className="mt-6 inline-flex flex-wrap rounded-xl bg-white/15 p-1 gap-1" data-testid="startup-track-toggle">
          {[["care", "Care & children's services"], ["aesthetics", "Clinical aesthetics clinic"], ["healthcare", "GP practice & hospital"], ["fostering", "Fostering agency"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTrack(id)} data-testid={`startup-track-${id}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${track === id ? "bg-white text-orange-700" : "text-white/90 hover:bg-white/10"}`}>
              {label}
            </button>
          ))}
        </div>
        {user && (
          <div className="mt-6 max-w-md">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Your progress</span><span className="font-semibold">{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="relative">
          <div className="absolute left-[27px] top-4 bottom-4 w-px bg-border hidden sm:block" />
          <div className="space-y-4">
            {steps.map((step) => {
              const done = progress.includes(step.id);
              return (
                <div key={step.id} data-testid={`startup-step-${step.id}`}
                  className="relative flex gap-4 bg-card border border-border rounded-2xl p-6 shadow-sm">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 font-display font-bold text-lg ${
                    done ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>
                    {done ? <Check className="w-6 h-6" /> : step.order}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display font-semibold text-lg">{step.title}</h3>
                      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer shrink-0">
                        <Checkbox checked={done} onCheckedChange={(v) => toggle(step.id, !!v)}
                          data-testid={`startup-step-checkbox-${step.order}`} />
                        <span className="hidden sm:inline">Done</span>
                      </label>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 mb-3">{step.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {step.tips.map((t) => (
                        <span key={t} className="text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="startup-toolkit-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Package className="w-7 h-7 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl font-bold mb-1">New Start-up Registration Toolkit</h3>
          <p className="text-sm text-muted-foreground">
            Application guidance, Statement of Purpose builder, core policy set and mock interview prep — everything to register with confidence.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto">
          <Price value={toolkit?.price_gbp} className="font-display text-2xl font-extrabold text-center" testId="startup-toolkit-price" />
          <WebOnlyPurchase>
          <Button onClick={buyToolkit} disabled={buying} data-testid="buy-startup-toolkit-button">
            {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get the toolkit"}
          </Button>
          <PayByInvoiceLink lookupKey="startup_toolkit" title="New Start-up Registration Toolkit" amount={toolkit?.price_gbp ?? 1000} className="justify-center" />
          </WebOnlyPurchase>
          {toolkit && (
            <Button asChild variant="outline" size="sm" data-testid="startup-toolkit-preview-button">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/resources/${toolkit.id}/preview`} target="_blank" rel="noreferrer"><Eye className="w-4 h-4 mr-1.5" /> Preview pages</a>
            </Button>
          )}
        </div>
        </div>
        {toolkit?.toc?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border" data-testid="startup-toolkit-contents">
            <p className="text-sm font-semibold mb-3">What's inside · {toolkit.pages} pages</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {toolkit.toc.map((t, i) => (
                <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span> {t}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!user && (
        <div className="mt-6 flex items-center gap-2 justify-center text-sm text-muted-foreground">
          <Lock className="w-4 h-4" />
          <Link to="/register" className="text-primary font-medium hover:underline flex items-center gap-1">
            Create a free account to track progress <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
